/**
 * DYO Add component for adding products to Design Your Own
 * 
 * @module dyo-add
 * @version 1.0.0
 * @extends HTMLElement
 * 
 * Usage:
 * <dyo-add type="setting"></dyo-add>
 * <dyo-add type="diamond"></dyo-add>
 */

if (!customElements.get('dyo-add')) {
  class DYOAdd extends HTMLElement {

    /**
     * Define observed attributes
     */
    static get observedAttributes() {
      return ['type', 'label', 'disabled', 'handle', 'stone-id', 'collection-handle', 'products-count', 'compatibility'];
    }

    /**
     * Constructor for the DYOAdd element
     */
    constructor() {
      super();

      this.productsCount = 0;
      this.compatibility = true;

      this.boundClickHandler = this.handleClick.bind(this);

      // Create or get the compatible collection
      this.getCompatibleCollection().then(result => {

        if (!result) {
          return;
        }

        if (Number(result.productsCount) > 0) {
          this.productsCount = result.productsCount;
          this.classList.remove('hidden');
        } else {
          // Collection was just created (or is still being indexed by Shopify),
          // so productsCount is 0/undefined. Poll until it has products, then show.
          this.checkIfCompatibleCollectionHasProducts();
        }
      });
      this.checkSettingCompatibility();
    }

    /**
     * Connected callback - runs when element is added to DOM
     */
    connectedCallback() {
      this.render();
      this.addEventListener('click', this.boundClickHandler);
    }

    /**
     * Check the compatibility of the setting
     * 
     * @returns {boolean} The compatibility of the product and the stone
     */
    async checkSettingCompatibility() {

      const stoneId = this.stoneId ? this.stoneId : window.theme.dyo.data.diamond ? window.theme.dyo.data.diamond.id : null;
      const productHandle = this.handle ? this.handle : window.theme.dyo.data.setting ? window.theme.dyo.data.setting.handle : null;

      if (stoneId === null || productHandle === null) {
        return true;
      }

      if (this.getAttribute('stone-source') === 'shopify') {
        const product = await window.theme.product.loadProduct(productHandle);
        const shape = (this.getAttribute('stone-shape') || '').toLowerCase();
        const carat = this.getAttribute('stone-carat');
        if (!shape || !carat || !product?.compatibility) {
          this.compatibility = true;
        } else {
          const ref = `${shape}_min_carat_weight`;
          this.compatibility = parseFloat(product.compatibility[ref]) <= parseFloat(carat);
        }
        return this.compatibility;
      }

      const stoneType = window.theme.dyo.data.diamond ? window.theme.dyo.data.diamond.type : 'diamond';
      this.compatibility = await window.theme.dyo.checkCompatibility(productHandle, stoneId, stoneType);

      return this.compatibility;
    }

    /**
     * Get the compatible collection
     */
    async getCompatibleCollection() {

      let collection = null;

      if (this.type === 'diamond' && this.stoneId) {

        let stone;
        if (this.getAttribute('stone-source') === 'shopify') {
          stone = { carat: this.getAttribute('stone-carat'), shape: this.getAttribute('stone-shape') };
        } else {
          stone = await window.theme.stones.getStoneById(this.stoneId, 'diamond');
        }

        if (stone.carat && stone.shape) {

          // Make a request to get the compatible collection handle
          const result = await window.theme.dyo.getCompatibleSettingCollectionHandle('Engagement Ring', stone.shape, stone.carat);

          collection = result.data;

          this.collectionHandle = collection.handle;
          this.productsCount = collection.productsCount;
        }
      }

      return collection;
    }

    /**
     * Check if the compatible collection has products to display the button
     */
    checkIfCompatibleCollectionHasProducts() {

      let count = 0;

      const interval = setInterval(() => {

        // Products showed up (Shopify finished indexing the new collection) — reveal the button.
        if (Number(this.productsCount) > 0) {
          clearInterval(interval);
          this.classList.remove('hidden');
          return;
        }

        if (count >= 5) {
          clearInterval(interval);
          return;
        }

        count++;
        this.getCompatibleCollection();
      }, 2000);
    }

    /**
     * Disconnected callback - cleanup when element is removed
     */
    disconnectedCallback() {
      this.removeEventListener('click', this.boundClickHandler);
    }

    /**
     * Attribute changed callback
     */
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue) {
        this.render();
      }
    }

    /**
     * Get the compatibility
     */
    get compatibility() {
      return this.getAttribute('compatibility');
    }

    /**
     * Set the compatibility
     */
    set compatibility(value) {
      this.setAttribute('compatibility', value);
    }

    /**
     * Get the collection handle
     */
    get collectionHandle() {
      return this.getAttribute('collection-handle');
    }
    
    /**
     * Set the collection handle
     */
    set collectionHandle(value) {
      this.setAttribute('collection-handle', value);
    }

    /**
     * Get the products count
     */
    get productsCount() {
      return this.getAttribute('products-count');
    }

    /**
     * Set the products count
     */
    set productsCount(value) {
      this.setAttribute('products-count', value);
    }

    /**
     * Get the type of product (setting or diamond)
     */
    get type() {
      return this.getAttribute('type') || 'setting';
    }

    /**
     * Get the button label
     */
    get label() {

      if (this.type === 'diamond' && !theme.dyo.data.setting) {
        return 'Add to Ring';
      }

      if (theme.dyo.startedWith === '') {
        return this.type === 'setting' ? (this.getAttribute('label') || 'Add this Setting') : 'Add this Setting';
      }

      if (this.type === 'setting' && theme.dyo.startedWith === 'settings' && theme.dyo.nextStep === 'diamonds') {
        return this.type === 'setting' ? (this.getAttribute('label') || 'Add this Setting') : 'Add this Setting';
      }

      if (this.type === 'setting' && theme.dyo.startedWith === 'settings' && theme.dyo.nextStep === 'settings') {
        return this.type === 'setting' ? (this.getAttribute('label') || 'Add this Setting') : 'Add this Setting';
      }

      return 'Complete Your Ring';
    }

    /**
     * Get the stone ID
     */
    get stoneId() {
      return this.getAttribute('stone-id');
    }

    /**
     * Get disabled state
     */
    get disabled() {
      return this.hasAttribute('disabled');
    }

    /**
     * Set disabled state
     */
    set disabled(value) {
      if (value) {
        this.setAttribute('disabled', '');
      } else {
        this.removeAttribute('disabled');
      }
    }

    /**
     * Get the handle of the product
     */
    get handle() {
      return this.getAttribute('handle');
    }

    /**
     * Get setting as product data
     */
    async getSettingData() {
    
      const product = await window.theme.product.loadProduct(this.handle);
      const variant = product.variants[0];

      const form = this.closest('form');
      const formData = new FormData(form);
      const properties = {};

      // Get all form data
      for (const [key, value] of formData.entries()) {
        properties[key] = value;
      }

      // Extract product-level metafields for property selectors (size, warranty, engraving, etc.)
      const metafieldKeys = ['size', 'warranty', 'is_engravable', 'backing', 'chain_length', 'pendant_initials', 'number_of_initials', 'monogram_char'];
      const metafields = {};
      if (product.metafields) {
        for (const key of metafieldKeys) {
          const value = product.metafields[key];
          if (value !== undefined && value !== null && value !== '') {
            metafields[key] = value;
          }
        }
      }

      // TODO: Instead to use product here, we may need to use the selected variant to get the correct price
      // TODO: We may need to consider the product properties because it influences the final price (For example size)

      return {
        id: variant.id,
        handle: product.handle,
        title: product.title,
        price: variant.price,
        image: product.featured_image,
        images: (product.images || []),
        sku: variant.sku,
        handle: product.handle,
        size: formData.get('properties[_size]'),
        engraving: formData.get('properties[_Engraving]'),
        fontStyle: formData.get('properties[_Font Style]'),
        warranty: formData.get('properties[_warranty]'),
        appraisal: formData.get('properties[_appraisal]'),
        metafields
      };
    }

    /**
     * Extract diamond data from LooseDiamondsInformation component
     */
    extractDiamondData() {
      try {
        let diamondInfo = document.querySelector('loose-diamond-information, gemstone-information, moissanite-information, stone-information');
        if (diamondInfo && diamondInfo.data) {
          const data = diamondInfo.data;
          return {
            id: data.id || `diamond-${Date.now()}`,
            title: data.title || '',
            price: data.price,
            image: data.image || data.image_url  || '',
            sku: data.sku || data.id,
            carat: data.carat,
            color: data.color,
            clarity: data.clarity,
            cut: data.cut,
            shape: data.shape,
            type: data.category || data.stone_type,
            source: data.source || 'legacy',
            handle: data.handle || null,
            variantId: data.variantId || (data.source === 'shopify' ? data.id : null)
          };
        }
        
        throw new Error('Diamond data not available');
      } catch (error) {
        console.warn('Could not extract diamond data:', error);
        return this.getFallbackDiamondData();
      }
    }

    /**
     * Get fallback setting data
     */
    getFallbackSettingData() {
      return {
        id: `setting-${Date.now()}`,
        title: 'Ring Setting',
        price: '$0',
        image: 'https://placehold.co/100x100/e2e8f0/64748b?text=Setting',
        sku: null
      };
    }

    /**
     * Get fallback diamond data
     */
    getFallbackDiamondData() {
      return {
        id: `diamond-${Date.now()}`,
        title: 'Diamond',
        price: '$0',
        image: 'https://placehold.co/100x100/e2e8f0/64748b?text=Diamond',
        sku: null
      };
    }

    /**
     * Get shape name from shape ID
     */
    getShapeName(shapeId) {
      const shapes = {
        1: 'Princess',
        2: 'Cushion',
        3: 'Oval',
        4: 'Pear',
        5: 'Round',
        6: 'Marquise',
        7: 'Emerald',
        8: 'Radiant',
        9: 'Asscher',
        10: 'Heart'
      };
      return shapes[shapeId] || 'Round';
    }

    /**
     * Validate the form to make sure all fields are filled out
     */
    validateForm() {
      const form = this.closest('form');

      if (!form) return false;

      const isValid = form.checkValidity();

      if (!isValid) {
        form.reportValidity();
        return false;
      }

      return true;
    }

    /**
     * Handle click event
     */
    async handleClick(event) {
      event.preventDefault();

      if (this.disabled || !this.validateForm()) return;

      // Check if DYO API is available
      if (!window.theme?.dyo) {
        console.error('DYO API not available');
        this.showError('Design Your Own is not available');
        return;
      }

      try {
        this.setLoading(true);

        let productData;
        
        if (this.type === 'setting') {
          productData = await this.getSettingData();
          window.theme.dyo.setSetting(productData);
          this.showSuccess('Setting added to your custom ring!');
          
          // Show drawer with next step after successful setting addition
          setTimeout(() => {
            this.showSettingSelectedDrawer(productData);
          }, 1000); // Show drawer after success message
          
        } else if (this.type === 'diamond') {
          productData = this.extractDiamondData();
          
          await window.theme.dyo.setDiamond(productData);

          this.showSuccess('Diamond added to your custom ring!');

          // Show drawer with next step after successful diamond addition
          setTimeout(() => {
            this.showDiamondSelectedDrawer(productData);
          }, 1000); // Show drawer after success message

        } else {
          throw new Error(`Unknown type: ${this.type}`);
        }

        // Optional: Redirect to DYO page or show next step
        this.dispatchCustomEvent('dyo-product-added', {
          type: this.type,
          data: productData
        });

      } catch (error) {
        console.error('Error adding product to DYO:', error);
        this.showError(error.message || 'Could not add product to Design Your Own');
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * Set loading state
     */
    setLoading(loading) {
      const button = this.querySelector('button');
      if (button) {
        button.disabled = loading;
        if (loading) {
          button.classList.add('loading');
          this.originalText = button.textContent;
          button.textContent = 'Adding...';
        } else {
          button.classList.remove('loading');
          if (this.originalText) {
            button.textContent = this.originalText;
            this.originalText = null;
          }
        }
      }
    }

    /**
     * Show success message
     */
    showSuccess(message) {
      this.showMessage(message, 'success');
    }

    /**
     * Show error message
     */
    showError(message) {
      this.showMessage(message, 'error');
    }

    /**
     * Show temporary message
     */
    showMessage(message, type = 'info') {

      // Create or update message element
      let messageEl = this.querySelector('.dyo-message');
      if (!messageEl) {
        messageEl = document.createElement('div');
        messageEl.className = 'dyo-message text-xs mt-2 px-3 py-2 rounded';
        this.appendChild(messageEl);
      }

      // Set message and styling
      messageEl.textContent = message;
      messageEl.className = `dyo-message text-xs mt-2 px-3 py-2 rounded ${
        type === 'success' ? 'bg-green-100 text-green-800' :
        type === 'error' ? 'bg-red-100 text-red-800' :
        'bg-blue-100 text-blue-800'
      }`;

      // Auto-hide after 3 seconds
      setTimeout(() => {
        if (messageEl.parentNode === this) {
          this.removeChild(messageEl);
        }
      }, 3000);
    }

    /**
     * Dispatch custom event
     */
    dispatchCustomEvent(name, detail) {

      const event = new CustomEvent(name, {
        detail,
        bubbles: true,
        cancelable: true
      });

      this.dispatchEvent(event);
    }

    /**
     * Create and show drawer with setting selected message
     */
    showSettingSelectedDrawer(settingData) {

      if (!settingData) return;

      // Create the drawer if it doesn't exist
      if (!document.getElementById('dyo-setting-drawer')) {
        this.createSettingDrawer();
      }

      // Show the drawer
      this.openDrawer();
    }

    /**
     * Show the diamond selected drawer
     */
    showDiamondSelectedDrawer(diamondData) {

      if (!diamondData) return;

      // Create the drawer if it doesn't exist
      if (!document.getElementById('dyo-diamond-drawer')) {
        this.createDiamondDrawer();
      }

      // Show the drawer
      this.openDrawer('dyo-diamond-drawer');
    }

    /**
     * Create the diamond drawer element
     */
    createDiamondDrawer() {

      const drawerContainer = document.createElement('div');

      if (window.theme.dyo.nextStep === 'review') {

        drawerContainer.innerHTML = `
          <side-drawer id="dyo-diamond-drawer" side="center" width="560px">
            <div class="flex flex-col gap-5 px-6 pb-6">
              <div class="text-2xl uppercase">Congratulations</div>
              <p class="text-sm">Your Custom Ring is Ready!</p>
              <button type="button" class="btn btn-ghost dyo-review-btn">Review Your Ring</button>
              <p class="text-sm text-alert-error hidden dyo-review-error"></p>
            </div>
          </side-drawer>
        `;

        const drawer = drawerContainer.firstElementChild;
        document.body.appendChild(drawer);
        const btn = drawer.querySelector('.dyo-review-btn');
        if (btn) btn.addEventListener('click', () =>
          this.handleCreateAndRedirect(btn, drawer.querySelector('.dyo-review-error'))
        );
        return;

      } else {

        drawerContainer.innerHTML = `
          <side-drawer id="dyo-diamond-drawer" side="center" width="560px">
            <div class="flex flex-col gap-5 px-6 pb-6">
              <div class="text-2xl uppercase">Proceed To The Next Step</div>
              <p class="text-sm">Add a Setting to Complete Your Ring. Choose one of the options below:</p>
              <a href="/collections/design-your-own-engagement-ring" class="btn btn-ghost">Add an Engagement Ring Setting</a>
            </div>
          </side-drawer>
        `;
      }

      // Append the actual side-drawer element to body
      const drawer = drawerContainer.firstElementChild;
      document.body.appendChild(drawer);
    }

    /**
     * Create the side drawer element
     */
    createSettingDrawer() {

      // Create the drawer using innerHTML to properly instantiate the custom element
      const drawerContainer = document.createElement('div');

      if (window.theme.dyo.nextStep === 'review') {

        drawerContainer.innerHTML = `
          <side-drawer id="dyo-setting-drawer" side="center" width="560px">
            <div class="flex flex-col gap-5 px-6 pb-6">
              <div class="text-2xl uppercase">Congratulations</div>
              <p class="text-sm">Your Custom Ring is Ready!</p>
              <button type="button" class="btn btn-ghost dyo-review-btn">Review Your Ring</button>
              <p class="text-sm text-alert-error hidden dyo-review-error"></p>
            </div>
          </side-drawer>
        `;

        const drawer = drawerContainer.firstElementChild;
        document.body.appendChild(drawer);
        const btn = drawer.querySelector('.dyo-review-btn');
        if (btn) btn.addEventListener('click', () =>
          this.handleCreateAndRedirect(btn, drawer.querySelector('.dyo-review-error'))
        );
        return;

      } else {

        drawerContainer.innerHTML = `
          <side-drawer id="dyo-setting-drawer" side="center" width="560px">
            <div class="flex flex-col gap-3 px-6 pb-6">
              <div class="text-2xl uppercase">Proceed To The Next Step 2</div>
              <p class="text-sm">Add a Center Stone to Complete Your Ring. Choose one of the options below:</p>
              <a href="/collections/loose-diamonds" class="btn btn-ghost">Add a Natural Diamond</a>
              <a href="/collections/loose-diamonds?lab_grown=true" class="btn btn-ghost">Add a Lab Diamond</a>
              <a href="/collections/color-diamonds" class="btn btn-ghost">Add a Colored Diamond</a>
              <a href="/collections/moissanites-1" class="btn btn-ghost">Add a Center Moissanite</a>
              <a href="/collections/gemstones" class="btn btn-ghost">Add a Center Gemstone</a>
            </div>
          </side-drawer>
        `;
      }

      // Append the actual side-drawer element to body
      const drawer = drawerContainer.firstElementChild;
      document.body.appendChild(drawer);
    }

    /**
     * Handle "Review Your Ring" button — creates a real product and redirects
     */
    async handleCreateAndRedirect(btn, errorEl) {
      const originalText = btn.textContent;
      try {
        btn.disabled = true;
        btn.textContent = 'Creating your ring...';
        if (errorEl) errorEl.classList.add('hidden');

        const setting = window.theme.dyo.data.setting;
        const productData = await window.theme.dyo.createProduct();
        window.theme.dyo.setCreatedProduct(productData);

        // Build URL params to pre-select the properties chosen on the setting page
        const params = new URLSearchParams();
        if (setting) {
          if (setting.size) params.set('size', setting.size.split(':')[0]);
          if (setting.warranty) params.set('warranty', setting.warranty.split(':')[0]);
          if (setting.engraving) params.set('engraving_text', setting.engraving);
          if (setting.fontStyle) params.set('engraving_font', setting.fontStyle);
          if (setting.appraisal && setting.appraisal.split(':')[0] === 'true') params.set('appraisal', 'true');
        }

        const qs = params.toString();
        window.location.href = `/products/${productData.handle}${qs ? '?' + qs : ''}`;
      } catch (error) {
        console.error('DYO product creation failed — falling back to custom-ring:', error);
        window.location.href = window.theme.dyo.getShareableUrl();
      }
    }

    /**
     * Open the drawer
     */
    openDrawer(id = 'dyo-setting-drawer') {
      const drawer = document.getElementById(id);
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    }

    /**
     * Close the drawer
     */
    closeDrawer(id = 'dyo-setting-drawer') {
      const drawer = document.getElementById(id);
      if (drawer && typeof drawer.close === 'function') {
        drawer.close();
      }
    }

    /**
     * Render the component
     */
    render() {
      const disabled = this.disabled;

      if (this.compatibility === true || this.compatibility === 'true') {

        if (this.type === 'setting') {
          this.classList.remove('hidden');
        } else {
          this.productsCount > 0 ? this.classList.remove('hidden') : this.classList.add('hidden');
        }

      } else {

        this.classList.add('hidden');
      }

      this.innerHTML = `
        <button 
          type="submit" 
          class="btn w-full btn-action py-[22px] ${disabled ? 'opacity-50 cursor-not-allowed' : ''}"
          ${disabled ? 'disabled' : ''}
        >
          ${this.label}
        </button>
      `;
    }
  }

  // Define the custom element
  customElements.define('dyo-add', DYOAdd);
}
